import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getWidgetMeta, MASTER_FILTER_TYPES, resolveListenerMapping } from "@/lib/widgetCatalog";
import { filterWidgetTitle } from "@/components/bi/FilterWidgets";
import { X, MousePointerClick, Search, Plus, Copy, Trash2, Check, SlidersHorizontal, BarChart3, Pencil, AlertTriangle, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";

const NON_LISTENER = new Set(["text", "image", "container", "rich_text"]);

const newPid = () => `p_${Math.random().toString(36).slice(2, 8)}`;

export function masterLabel(w) {
  if (!w) return "";
  if (MASTER_FILTER_TYPES.includes(w.type) && w.type !== "multi_filter") return w.title || filterWidgetTitle(w);
  return w.title || getWidgetMeta(w.type)?.label || w.type;
}

// Profile name that says what it controls ("Region Filter"), never a bare "Default".
export function defaultProfileName(master) {
  const base = masterLabel(master) || "Filter";
  return /filter$/i.test(base) ? base : `${base} Filter`;
}

export function draftFromMaster(m) {
  const cfg = m.config || {};
  let profiles = Array.isArray(cfg.interaction_profiles) && cfg.interaction_profiles.length
    ? cfg.interaction_profiles.map((p) => ({ ...p, name: p.name === "Default" ? defaultProfileName(m) : p.name, interactions: { ...(p.interactions || {}) }, mappings: [...(p.mappings || [])] }))
    : [{ id: "default", name: defaultProfileName(m), mapping_mode: cfg.field_mappings?.length ? "custom" : "default", interactions: { ...(cfg.interactions || {}) }, mappings: [...(cfg.field_mappings || [])] }];
  const sel = profiles.some((p) => p.id === cfg.active_profile) ? cfg.active_profile : profiles[0].id;
  return { profiles, sel };
}

// Column select with an inline search box (item 79.6: mapping column search).
function ColSelect({ value, onChange, options, placeholder, testid }) {
  const [q, setQ] = useState("");
  const list = q ? options.filter((c) => c.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-7 text-[11px] bg-[color:var(--surface-2)]"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <div className="px-1 pb-1 sticky top-0 bg-[color:var(--surface)] z-10">
          <Input data-testid={testid ? `${testid}-search` : undefined} value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()} placeholder="Search…" className="h-6 text-[11px] bg-[color:var(--surface-2)]" />
        </div>
        {list.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        {!list.length && <p className="px-2 py-1 text-[10px] text-[color:var(--ink-3)]">No columns match.</p>}
      </SelectContent>
    </Select>
  );
}

// Interaction Matrix (items 12/15): masters (filters + chart sources) | profiles | listeners +
// per-listener column mapping. Drafted edits persist on Update; reopening restores the saved
// configuration for the selected master.
export function InteractionsPanel({ open, onOpenChange, widgets, datasets = [], sourceId, onSourceChange, onSaveMaster }) {
  const [qMaster, setQMaster] = useState("");
  const [qListener, setQListener] = useState("");
  const [draft, setDraft] = useState(null);
  const [editingName, setEditingName] = useState(null);

  const masters = useMemo(() => {
    const all = widgets.filter((w) => w.dataset_id && !NON_LISTENER.has(w.type));
    const flt = (list) => qMaster ? list.filter((w) => masterLabel(w).toLowerCase().includes(qMaster.toLowerCase())) : list;
    return {
      filters: flt(all.filter((w) => MASTER_FILTER_TYPES.includes(w.type))),
      charts: flt(all.filter((w) => !MASTER_FILTER_TYPES.includes(w.type))),
    };
  }, [widgets, qMaster]);

  const master = widgets.find((w) => w.id === sourceId) || null;
  const isFilterMaster = master && MASTER_FILTER_TYPES.includes(master.type);
  const savedKey = master ? JSON.stringify([master.config?.interaction_profiles, master.config?.interactions, master.config?.field_mappings, master.config?.active_profile]) : "";

  // Auto-select the first master filter when opened without a selection.
  useEffect(() => {
    if (!open) return;
    if (!master) {
      const first = masters.filters[0] || masters.charts[0];
      if (first) onSourceChange(first.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceId, widgets.length]);
  // (Re)load the draft from the SAVED config whenever the panel opens, the master changes,
  // or the saved config changes (after Update) — so what you see is what is active.
  useEffect(() => {
    if (!open || !master) { if (!master) setDraft(null); return; }
    setDraft(draftFromMaster(master));
    setEditingName(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceId, savedKey]);

  const listeners = useMemo(() => {
    let list = widgets.filter((w) => w.id !== sourceId && w.dataset_id && !NON_LISTENER.has(w.type) && !MASTER_FILTER_TYPES.includes(w.type));
    if (qListener) list = list.filter((w) => (w.title || w.type).toLowerCase().includes(qListener.toLowerCase()));
    return list;
  }, [widgets, sourceId, qListener]);

  if (!open) return null;

  const pickMaster = (w) => { onSourceChange(w.id); };
  const prof = draft?.profiles.find((p) => p.id === draft.sel) || null;
  const patchProfile = (patch) => setDraft((d) => ({ ...d, profiles: d.profiles.map((p) => p.id === d.sel ? { ...p, ...patch } : p) }));
  const setMode = (tid, mode) => {
    const cur = { ...(prof?.interactions || {}) };
    if (mode === "filter") delete cur[tid]; else cur[tid] = mode;
    patchProfile({ interactions: cur });
  };
  const masterFields = master?.type === "multi_filter"
    ? (master.config?.fields || []).map((f) => f.field).filter(Boolean)
    : [master?.config?.dimensions?.[0]?.field].filter(Boolean);
  const masterField = masterFields[0] || "";
  const masterDs = datasets.find((d) => d.id === master?.dataset_id);
  const masterCols = (masterDs?.schema || []).map((c) => c.name);
  const dashboardDatasets = [...new Set(widgets.map((w) => w.dataset_id).filter(Boolean))]
    .map((id) => datasets.find((d) => d.id === id)).filter(Boolean);
  const dsName = (id) => datasets.find((d) => d.id === id)?.name || id || "—";

  // A "virtual master" carrying the DRAFT mappings so status badges reflect unsaved edits.
  const draftMaster = master && prof ? { ...master, config: { ...(master.config || {}), field_mappings: prof.mapping_mode === "custom" ? prof.mappings : [] } } : master;
  const statusFor = (t) => {
    if (!draftMaster || !masterField) return { ok: false, via: "none", reason: "master has no field" };
    return resolveListenerMapping(draftMaster, t, masterField, datasets);
  };
  const activeListeners = listeners.filter((t) => (prof?.interactions?.[t.id] || "filter") !== "none");
  const unmapped = prof ? activeListeners.filter((t) => !statusFor(t).ok) : [];

  const savedDraft = master ? draftFromMaster(master) : null;
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(savedDraft);

  const update = () => {
    if (!master || !draft || !prof) return;
    onSaveMaster(master.id, {
      interactions: prof.interactions,
      field_mappings: prof.mapping_mode === "custom" ? prof.mappings.filter((m) => m.source_col && m.target_col && (m.target_dataset || m.target_widget)) : [],
      interaction_profiles: draft.profiles,
      active_profile: draft.sel,
    });
  };

  const addMappingFor = (t) => {
    patchProfile({
      mapping_mode: "custom",
      mappings: [...(prof.mappings || []), { source_col: masterField, target_widget: t.id, target_dataset: t.dataset_id, target_col: "" }],
    });
  };

  const MasterRow = ({ w, icon: FallbackIcon }) => {
    const Icon = getWidgetMeta(w.type)?.icon || FallbackIcon;
    const nProfiles = Array.isArray(w.config?.interaction_profiles) ? w.config.interaction_profiles.length : 0;
    const nOff = Object.values(w.config?.interactions || {}).filter((m) => m === "none").length;
    return (
      <button key={w.id} data-testid="ix-master-row" onClick={() => pickMaster(w)}
        className={cn("w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-xs",
          sourceId === w.id ? "bg-[color:rgba(43,78,255,0.12)] text-[color:var(--brand-ocean)] font-medium" : "text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]")}>
        <Icon className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate flex-1">{masterLabel(w)}</span>
        {(nOff > 0 || nProfiles > 1) && <span className="text-[9px] px-1 rounded bg-[color:var(--surface-2)] text-[color:var(--ink-3)] shrink-0" title={`${nOff} listener(s) off · ${nProfiles || 1} profile(s)`}>{nOff > 0 ? `−${nOff}` : `${nProfiles}p`}</span>}
      </button>
    );
  };

  const Badge = ({ t }) => {
    const st = statusFor(t);
    const off = (prof?.interactions?.[t.id] || "filter") === "none";
    if (off) return <span className="text-[9px] text-[color:var(--ink-3)] shrink-0">not affected</span>;
    if (!st.ok) return (
      <span data-testid="ix-listener-warning" className="flex items-center gap-1 text-[9px] text-[#B45309] shrink-0" title={st.reason}>
        <AlertTriangle className="w-3 h-3" />no mapping
        <button data-testid="ix-listener-map-btn" onClick={() => addMappingFor(t)} className="underline hover:text-[color:var(--brand-ocean)]">Map…</button>
      </span>
    );
    const txt = st.via === "same" ? `→ ${st.field}` : st.via === "name" ? `→ ${st.field} (by name)` : `→ ${st.field} (custom)`;
    return <span data-testid="ix-listener-mapped" className="text-[9px] text-[color:var(--ink-3)] truncate max-w-[160px] shrink-0" title={`${masterField} ${txt}`}>{txt}</span>;
  };

  return (
    <div data-testid="interactions-panel" className="fixed right-4 top-16 bottom-4 w-[760px] max-w-[94vw] z-40 rounded-2xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] shadow-xl flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[color:var(--glass-border)] shrink-0">
        <span className="font-display text-sm text-[color:var(--ink)] flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-[color:var(--brand-ocean)]" />Interaction Matrix</span>
        <span className="flex items-center gap-2">
          {dirty && <span data-testid="ix-dirty" className="text-[10px] px-2 py-0.5 rounded-full bg-[color:rgba(245,158,11,0.15)] text-[#B45309]">Unsaved changes</span>}
          <button data-testid="ix-close" onClick={() => onOpenChange(false)} className="p-1 rounded-md text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]"><X className="w-4 h-4" /></button>
        </span>
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* Masters */}
        <div className="w-[200px] shrink-0 border-r border-[color:var(--glass-border)] flex flex-col min-h-0">
          <div className="p-2 shrink-0">
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
              <Input data-testid="ix-master-search" value={qMaster} onChange={(e) => setQMaster(e.target.value)} placeholder="Find widget…" className="h-7 pl-6 text-xs bg-[color:var(--surface-2)]" />
            </div>
          </div>
          <div className="flex-1 overflow-auto px-2 pb-2 space-y-0.5">
            <p className="text-[9px] uppercase tracking-wide text-[color:var(--ink-3)] px-1 pt-1 flex items-center gap-1"><SlidersHorizontal className="w-2.5 h-2.5" />Master filters</p>
            {masters.filters.map((w) => <MasterRow key={w.id} w={w} icon={SlidersHorizontal} />)}
            {!masters.filters.length && <p className="text-[10px] text-[color:var(--ink-3)] px-1">No filter widgets yet — add a Filter, Combo Box, List Box or Date Filter from the library.</p>}
            <p className="text-[9px] uppercase tracking-wide text-[color:var(--ink-3)] px-1 pt-2 flex items-center gap-1"><BarChart3 className="w-2.5 h-2.5" />Chart cross-filter sources</p>
            {masters.charts.map((w) => <MasterRow key={w.id} w={w} icon={BarChart3} />)}
          </div>
        </div>

        {!master || !draft || !prof ? (
          <div className="flex-1 flex items-center justify-center text-xs text-[color:var(--ink-3)] px-8 text-center">Pick a master widget on the left to configure which widgets it filters.</div>
        ) : (
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            <div className="flex-1 min-h-0 flex">
              {/* Profiles */}
              <div className="w-[190px] shrink-0 border-r border-[color:var(--glass-border)] flex flex-col min-h-0">
                <div className="flex items-center justify-between px-2.5 py-2 shrink-0">
                  <span className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Filter profiles</span>
                  <button data-testid="ix-profile-add" title="New profile" onClick={() => setDraft((d) => { const p = { id: newPid(), name: `${masterLabel(master)} – Profile ${d.profiles.length + 1}`, mapping_mode: "default", interactions: {}, mappings: [] }; return { ...d, profiles: [...d.profiles, p], sel: p.id }; })}
                    className="p-1 rounded text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]"><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex-1 overflow-auto px-2 pb-2 space-y-0.5">
                  {draft.profiles.map((p) => (
                    <div key={p.id} data-testid="ix-profile-row"
                      className={cn("group flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs cursor-pointer",
                        draft.sel === p.id ? "bg-[color:rgba(24,169,135,0.12)] text-[color:var(--ink)]" : "text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]")}
                      onClick={() => setDraft((d) => ({ ...d, sel: p.id }))}>
                      {editingName === p.id ? (
                        <Input autoFocus defaultValue={p.name} data-testid="ix-profile-name-input"
                          onClick={(e) => e.stopPropagation()}
                          onBlur={(e) => { const v = e.target.value.trim(); setDraft((d) => ({ ...d, profiles: d.profiles.map((x) => x.id === p.id ? { ...x, name: v || x.name } : x) })); setEditingName(null); }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditingName(null); }}
                          className="h-6 text-xs bg-[color:var(--surface)]" />
                      ) : (
                        <>
                          <span className="truncate flex-1" title={p.name}>{p.name}</span>
                          {draft.sel === p.id && <Check className="w-3 h-3 text-[color:var(--brand-mint)] shrink-0" />}
                          <span className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                            <button data-testid="ix-profile-rename" title="Rename" onClick={(e) => { e.stopPropagation(); setEditingName(p.id); }} className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><Pencil className="w-3 h-3" /></button>
                            <button data-testid="ix-profile-dup" title="Duplicate" onClick={(e) => { e.stopPropagation(); setDraft((d) => { const c = { ...p, id: newPid(), name: `${p.name} copy`, interactions: { ...p.interactions }, mappings: [...p.mappings] }; return { ...d, profiles: [...d.profiles, c], sel: c.id }; }); }} className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><Copy className="w-3 h-3" /></button>
                            {draft.profiles.length > 1 && (
                              <button data-testid="ix-profile-del" title="Delete" onClick={(e) => { e.stopPropagation(); setDraft((d) => { const rest = d.profiles.filter((x) => x.id !== p.id); return { ...d, profiles: rest, sel: d.sel === p.id ? rest[0].id : d.sel }; }); }} className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3 h-3" /></button>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  ))}
                  <p className="text-[9px] text-[color:var(--ink-3)] px-1 pt-1">The ✓ profile is active on the dashboard.</p>
                </div>
              </div>

              {/* Listeners */}
              <div className="flex-1 min-w-0 flex flex-col min-h-0">
                <div className="p-2 shrink-0 flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
                    <Input data-testid="ix-listener-search" value={qListener} onChange={(e) => setQListener(e.target.value)} placeholder="Find listener…" className="h-7 pl-6 text-xs bg-[color:var(--surface-2)]" />
                  </div>
                  <span data-testid="ix-listener-count" className="text-[10px] text-[color:var(--ink-3)] shrink-0">{activeListeners.length}/{listeners.length} on</span>
                  <button data-testid="ix-all-on" onClick={() => patchProfile({ interactions: {} })} className="text-[10px] text-[color:var(--brand-ocean)] hover:underline shrink-0">All on</button>
                  <button data-testid="ix-all-off" onClick={() => patchProfile({ interactions: Object.fromEntries(listeners.map((l) => [l.id, "none"])) })} className="text-[10px] text-[color:var(--ink-3)] hover:underline shrink-0">All off</button>
                </div>
                <div className="flex-1 overflow-auto px-2 pb-2 space-y-0.5">
                  {listeners.map((t) => {
                    const mode = prof.interactions[t.id] || "filter";
                    const Icon = getWidgetMeta(t.type)?.icon || BarChart3;
                    return (
                      <div key={t.id} data-testid="ix-listener-row" data-mode={mode} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[color:var(--surface-2)]">
                        {isFilterMaster ? (
                          <button data-testid="ix-listener-check" role="checkbox" aria-checked={mode !== "none"}
                            onClick={() => setMode(t.id, mode === "none" ? "filter" : "none")}
                            className={cn("w-4 h-4 rounded border flex items-center justify-center shrink-0",
                              mode !== "none" ? "bg-[color:var(--brand-ocean)] border-[color:var(--brand-ocean)]" : "border-[color:var(--glass-border)]")}>
                            {mode !== "none" && <Check className="w-3 h-3 text-white" />}
                          </button>
                        ) : null}
                        <Icon className="w-3.5 h-3.5 shrink-0 text-[color:var(--ink-3)]" />
                        <span className="text-xs text-[color:var(--ink-2)] truncate flex-1 min-w-0">{t.title || getWidgetMeta(t.type)?.label || t.type}
                          {t.dataset_id !== master.dataset_id && <span className="text-[9px] text-[color:var(--ink-3)]"> · {dsName(t.dataset_id)}</span>}
                        </span>
                        <Badge t={t} />
                        {!isFilterMaster && (
                          <span className="flex items-center gap-0.5 shrink-0">
                            {["filter", "highlight", "none"].map((m) => (
                              <button key={m} data-testid={`ix-mode-${m}`} onClick={() => setMode(t.id, m)}
                                className={cn("text-[10px] px-2 py-0.5 rounded-full border",
                                  mode === m ? "bg-[color:var(--brand-ocean)] text-white border-[color:var(--brand-ocean)]" : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)] border-[color:var(--glass-border)]")}>
                                {m}
                              </button>
                            ))}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {!listeners.length && <p className="text-[10px] text-[color:var(--ink-3)] px-2 py-2">No listener widgets match.</p>}
                </div>
              </div>
            </div>

            {/* Column mapping */}
            <div className="shrink-0 border-t border-[color:var(--glass-border)] px-3 py-2 space-y-1.5 max-h-[210px] overflow-auto">
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Column mapping</span>
                {["default", "custom"].map((m) => (
                  <label key={m} className="flex items-center gap-1 text-[11px] text-[color:var(--ink-2)] cursor-pointer">
                    <input type="radio" data-testid={`ix-mapping-${m}`} checked={(prof.mapping_mode || "default") === m}
                      onChange={() => patchProfile({ mapping_mode: m, mappings: m === "custom" && !prof.mappings.length && masterField ? [{ source_col: masterField, target_widget: "", target_dataset: "", target_col: "" }] : prof.mappings })} />
                    {m === "default" ? "Default (match by name)" : "Custom"}
                  </label>
                ))}
                {prof.mapping_mode === "custom" && (
                  <button data-testid="ix-mapping-add" onClick={() => patchProfile({ mappings: [...prof.mappings, { source_col: masterField, target_widget: "", target_dataset: "", target_col: "" }] })}
                    className="ml-auto text-[10px] text-[color:var(--brand-ocean)] hover:underline flex items-center gap-0.5"><Plus className="w-3 h-3" />Add mapping</button>
                )}
              </div>
              {prof.mapping_mode === "custom" ? (
                <div className="space-y-1">
                  <div className="grid grid-cols-[1fr_1.1fr_1fr_1fr_24px] gap-1.5 text-[9px] uppercase tracking-wide text-[color:var(--ink-3)]">
                    <span>Source column</span><span>Listener widget</span><span>Target dataset</span><span>Target column</span><span />
                  </div>
                  {prof.mappings.map((mp, i) => {
                    const tw = widgets.find((w) => w.id === mp.target_widget);
                    const tdsId = tw?.dataset_id || mp.target_dataset;
                    const tds = datasets.find((d) => d.id === tdsId);
                    const setRow = (patch) => patchProfile({ mappings: prof.mappings.map((x, j) => j === i ? { ...x, ...patch } : x) });
                    return (
                      <div key={i} data-testid="ix-mapping-row" className="grid grid-cols-[1fr_1.1fr_1fr_1fr_24px] gap-1.5 items-center">
                        <ColSelect testid="ix-map-source-col" value={mp.source_col} onChange={(v) => setRow({ source_col: v })} options={masterCols.length ? masterCols : masterFields} placeholder="Column" />
                        <Select value={mp.target_widget || "__any"} onValueChange={(v) => { const w = widgets.find((x) => x.id === v); setRow({ target_widget: v === "__any" ? "" : v, ...(w ? { target_dataset: w.dataset_id, target_col: "" } : {}) }); }}>
                          <SelectTrigger data-testid="ix-map-target-widget" className="h-7 text-[11px] bg-[color:var(--surface-2)]"><SelectValue placeholder="Any widget" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__any">Any widget on dataset</SelectItem>
                            {listeners.map((l) => <SelectItem key={l.id} value={l.id}>{l.title || getWidgetMeta(l.type)?.label || l.type}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={tdsId || ""} onValueChange={(v) => setRow({ target_dataset: v, target_col: "" })} disabled={!!tw}>
                          <SelectTrigger data-testid="ix-map-target-dataset" className="h-7 text-[11px] bg-[color:var(--surface-2)]"><SelectValue placeholder="Dataset" /></SelectTrigger>
                          <SelectContent>{dashboardDatasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <ColSelect testid="ix-map-target-col" value={mp.target_col} onChange={(v) => setRow({ target_col: v })} options={[...(tds?.schema || []).map((c) => c.name), ...(tds?.calc_fields || []).map((c) => c.name)]} placeholder="Column" />
                        <button data-testid="ix-mapping-remove" onClick={() => patchProfile({ mappings: prof.mappings.filter((_, j) => j !== i) })} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
                      </div>
                    );
                  })}
                  {!prof.mappings.length && <p className="text-[10px] text-[color:var(--ink-3)]">No custom mappings yet — add one, or click “Map…” next to a listener.</p>}
                </div>
              ) : (
                <div className="space-y-0.5" data-testid="ix-default-mapping-table">
                  <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-1.5 text-[9px] uppercase tracking-wide text-[color:var(--ink-3)]">
                    <span>Listener</span><span>Source column</span><span>Target dataset</span><span>Target column</span>
                  </div>
                  {activeListeners.slice(0, 12).map((t) => {
                    const st = statusFor(t);
                    return (
                      <div key={t.id} className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-1.5 text-[10px] text-[color:var(--ink-2)] items-center">
                        <span className="truncate">{t.title || t.type}</span>
                        <span className="truncate font-mono">{masterField || "—"}</span>
                        <span className="truncate">{dsName(t.dataset_id)}</span>
                        <span className={cn("truncate font-mono flex items-center gap-1", !st.ok && "text-[#B45309]")}>{st.ok ? st.field : <><AlertTriangle className="w-3 h-3" />unavailable</>}</span>
                      </div>
                    );
                  })}
                  {activeListeners.length > 12 && <p className="text-[9px] text-[color:var(--ink-3)]">+{activeListeners.length - 12} more listeners</p>}
                  {!activeListeners.length && <p className="text-[10px] text-[color:var(--ink-3)]">No listener is selected — this filter affects nothing until you tick widgets above.</p>}
                </div>
              )}
              {unmapped.length > 0 && (
                <p data-testid="ix-unmapped-warning" className="text-[10px] text-[#B45309] flex items-center gap-1"><AlertTriangle className="w-3 h-3 shrink-0" />{unmapped.length} selected listener{unmapped.length > 1 ? "s" : ""} cannot receive “{masterField}” — the filter is skipped for them (never applied globally). Use Custom mapping to set a target column.</p>
              )}
            </div>

            <div className="shrink-0 border-t border-[color:var(--glass-border)] px-3 py-2 flex items-center justify-between gap-2">
              <span className="text-[10px] text-[color:var(--ink-3)] truncate flex items-center gap-1"><Link2 className="w-3 h-3" />Master: <span className="font-medium text-[color:var(--ink-2)]">{masterLabel(master)}</span> · profile “{prof.name}” · {activeListeners.length} listener{activeListeners.length === 1 ? "" : "s"}</span>
              <Button data-testid="ix-update" size="sm" className="bg-[color:var(--brand-ocean)] text-white h-7" onClick={update} disabled={!dirty}>Update</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
